import axios, { AxiosProgressEvent, AxiosError } from 'axios';
import { computed, ref } from 'vue';
import { useLoginStore } from './store/login';
import { useUsersStore, JoinUser } from './store/users';
import { useDicomStore } from './store/dicom';
import { useLogStore } from './store/log';
import { useDiskStore } from './store/disk';
import { useTimerStore } from './store/timer';
import { useReadingResultStore } from './store/readingResult';
import { usePopupStore } from './store/popup';
import { useViewerStore } from './store/viewer';
import * as cornerstone from 'cornerstone-core'
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader'
import * as dicomParser from 'dicom-parser'
import { usePacsStore, PacsItem } from './store/pacs';

export const PROJECT_TYPE = "TDM";
export const LOGIN_SERVER_URL = '/auth'; // nginx
export const TUBA_SERVER_URL = '/api';
export const baseURL = "";

cornerstone.registerImageLoader('wadouri', cornerstoneWADOImageLoader.loadImage)
cornerstoneWADOImageLoader.external.cornerstone = cornerstone
cornerstoneWADOImageLoader.external.dicomParser = dicomParser
cornerstoneWADOImageLoader.configure({})

/**
 * LOGIN SERVER - checkLastLoginToken
 */
export async function checkLastLoginToken() {
  try {
    const token = useLoginStore().getAccessToken;
    const userId = useUsersStore().getLoginUser.data.user_id;
    const response = await axios.get(`${LOGIN_SERVER_URL}/checkLastLoginToken`, {
      params: {
        "user_id": userId,
        "login_type": PROJECT_TYPE
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.data.result) {
      usePopupStore().togglePopup('sessionConflict', true);
    }
    return response.data.result
  } catch (error: any) {
    console.error('Error fetching posts:', error);
    return { state: false, data: error.response.data.detail }
  }
}

/**
 * LOGIN SERVER - login
 */
export async function login(userId: string, passwd: string) {
  try {
    const response = await axios.post(`${LOGIN_SERVER_URL}/login`, {
      "user_id": userId,
      "passwd": passwd,
      "login_type": PROJECT_TYPE
    });
    if (response.data?.result == "Already login") {
      return { state: false, data: response.data.result }
    }
    return { state: true, data: response.data }
  } catch (error: any) {
    console.error('Error fetching posts:', error);
    return { state: false, data: error.response.data.detail }
  }
}

/**
 * LOGIN SERVER - logout
 */
export async function logout() {
  try {
    const token = useLoginStore().getAccessToken;
    const userId = useUsersStore().getLoginUser.data.user_id;

    const response = await axios.post(`${LOGIN_SERVER_URL}/logout`, {
      "user_id": userId,
      "passwd": sessionStorage.getItem('user_passwd'),
      "login_type": PROJECT_TYPE
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - getAllUserData
 */
export async function getAllUserData() {
  const token = useLoginStore().getAccessToken;
  try {
    const response = await axios.get(`${LOGIN_SERVER_URL}/getAllUserData`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
}

/**
 * LOGIN SERVER - getUserData
 */
export async function getUserData() {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.get(`${LOGIN_SERVER_URL}/getUserData`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - setUserData
 */
export async function setUserData(userData: JoinUser) {
  try {
    const data = userData.data;
    const response = await axios.post(`${LOGIN_SERVER_URL}/setUserData`, {
      "user_id": data.user_id,
      "passwd": userData.passwd,
      "group": {
        "group_id": data.group.group_id,
        "level": data.group.level,
        "name": data.group.name
      },
      "name": data.name,
      "number": data.number,
      "email": data.email,
      "division": data.division
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - checkRepetition
 */
export async function checkRepetition(inputId: string) {
  try {
    const response = await axios.get(`${LOGIN_SERVER_URL}/checkRepetition`, {
      params: {
        "user_id": inputId
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - removeUserData
 */
export async function removeUserData(userData: JoinUser) {
  try {
    const token = useLoginStore().getAccessToken;
    const data = userData.data;
    const response = await axios.post(`${LOGIN_SERVER_URL}/removeUserData`, {
      "user_id": data.user_id,
      "passwd": userData.passwd,
      "group": {
        "group_id": data.group.group_id,
        "level": data.group.level,
        "name": data.group.name
      },
      "name": data.name,
      "number": data.number,
      "email": data.email,
      "division": data.division
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - updateUserData
 */
export async function updateUserData(userData: JoinUser) {
  try {
    const token = useLoginStore().getAccessToken;
    const data = userData.data;
    const response = await axios.post(`${LOGIN_SERVER_URL}/updateUserData`, {
      "user_id": data.user_id,
      "passwd": userData.passwd,
      "group": {
        "group_id": data.group.group_id,
        "level": data.group.level,
        "name": data.group.name
      },
      "name": data.name,
      "number": data.number,
      "email": data.email,
      "division": data.division
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - getGroups
 */
export async function getGroups() {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.get(`${LOGIN_SERVER_URL}/getGroups`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - addGroups
 */
export async function addGroups(groupData: any) {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.post(`${LOGIN_SERVER_URL}/addGroups`, {
      "group_id": 0,
      "level": 0,
      "name": "string"
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - deleteGroups
 */
export async function deleteGroups(groupData: any) {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.post(`${LOGIN_SERVER_URL}/deleteGroups`, {
      "group_id": 0,
      "level": 0,
      "name": "string"
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - updateGroups
 * @param group_id PK. group_id를 기준으로 level, name이 업데이트 됨
 */
export async function updateGroups(groupData: any) {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.post(`${LOGIN_SERVER_URL}/updateGroups`, {
      "group_id": 0,
      "level": 0,
      "name": "string"
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - getSystemLogs
 */
export async function getSystemLogs() {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.get(`${LOGIN_SERVER_URL}/getSystemLogs`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - changePasswd
 * @param user_id 사용자 ID
 * 관리자 계정으로 접속 시 사용할 수 있는 기능
 * 사용자의 비밀번호를 ID와 동일한 값으로 변경한다.
 */
export async function changePasswd(groupData: any) {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.post(`${LOGIN_SERVER_URL}/changePasswd`, {
      "user_id": sessionStorage.getItem('user_id')
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    },);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - checkTokenExpire
 */
export async function checkTokenExpire() {
  try {
    const token = useLoginStore().getAccessToken;
    const response = await axios.get(`${LOGIN_SERVER_URL}/checkTokenExpire`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * LOGIN SERVER - recreateToken
 */
export async function recreateToken() {
  try {
    const response = await axios.get(`${LOGIN_SERVER_URL}/recreateToken`, {
      params: {
        "user_id": "string",
        "passwd": "string",
        "login_type": PROJECT_TYPE
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * NGTUBE SERVER
 * @method getDicomList
 * @description 주어진 사용자 ID에 대해 DICOM 파일 목록을 반환하는 비동기 함수
 * @returns {Promise<Array>} DICOM 파일 목록을 담고 있는 배열. 실패 시 빈 배열을 반환
 * @throws {AxiosError} Axios 요청 중 오류가 발생할 경우 예외가 발생
 */
export async function getDicomList() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const withCAD = useUsersStore().getWithCAD;

  try {
    useLoginStore().resetLogoutTimer();
    const response = await axios.get(`${TUBA_SERVER_URL}/file/get_dicom_list`, {
      params: {
        use_cad: withCAD,
        user_id: userId
      },
    });
    useLogStore().endTask('get dicom list 기능이 성공적으로 실행되었습니다.');
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `dicom upload 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('dicom upload 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
  }
}

/**
 * NGTUBE SERVER
 * @method dicomUpload
 */
export async function dicomUpload(uploadProgressCallback: (progress: number) => void) {
  const userId = useUsersStore().getLoginUser.data.user_id;
  const uploadedDicomFiles = useDicomStore().getFile; // Pinia 스토어에서 파일 가져오기
  const withCAD = useUsersStore().getWithCAD;
  const diskInfo = useDiskStore().getDiskInfoData;
  const fiveHundredMB = 300 * 1024 * 1024;

  for (let i = 0; i < uploadedDicomFiles.length; i++) {
    const isSessionValid = await checkLastLoginToken();
    if (!isSessionValid) { return }

    if (diskInfo.free > fiveHundredMB) {
      const formData = new FormData();
      formData.append('file', uploadedDicomFiles[i]);
      formData.append('user_id', userId);
      formData.append('use_cad', withCAD);

      try {
        useLoginStore().resetLogoutTimer();
        useLogStore().startTask();
        const response = await axios.post(`${TUBA_SERVER_URL}/file/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'json',
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              uploadProgressCallback(progress);
            }
          },
        });
        useLogStore().endTask('dicom upload 기능이 성공적으로 실행되었습니다.');
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          const { status, data } = error.response;
          const detail = data?.detail; // detail이 배열일 경우 대비

          const message = `dicom upload 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.message}, Type: ${detail?.status})`;
          useLogStore().endTask(message);
          console.error(message);
          return false;
        } else {
          // 예상치 못한 에러 처리
          useLogStore().endTask('dicom upload 기능 사용 중 오류가 발생하였습니다.');
          console.error("Unexpected Error:", error);
          return false;
        }
      }
    } else {
      useDicomStore().insertFailedFile(uploadedDicomFiles[i]);
      useDicomStore().decreasePendingCount();
    }
  }
  useDicomStore().checkFailedFileExist();
}

/**
 * NGTUBE SERVER
 * @method deleteDicomFiles
 */
export async function deleteDicomFiles() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const selectedList = useDicomStore().getSearchRes;
  const withCAD = useUsersStore().getWithCAD;
  let targetList = [];

  for (let i = 0; i < selectedList.length; i++) {
    if (selectedList[i].selected && (selectedList[i].status === 'Completed' || selectedList[i].status === 'Shared Existing' || selectedList[i].status === 'Failed' || !withCAD)) {
      targetList.push(selectedList[i].unique_filename);
    }
  }

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.delete(`${TUBA_SERVER_URL}/file/delete_dicom_files`, {
      headers: {
        'Content-Type': 'application/json'
      },
      data: {
        user_id: userId,
        unique_filenames: targetList
      }
    });

    if (response.data) {
      return true;
    }
    useLogStore().endTask('delete dicom files 기능이 성공적으로 실행되었습니다.');
    // getDiskInfo();
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `delete dicom files 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('delete dicom files 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }

    return false;
  }
}

/**
 * NGTUBE SERVER
 * @method selectFile
 */
let controller: AbortController | null = null;
let currentRequestId = 0; // 요청 ID 추적용
export async function selectFile() {
  const userId = useUsersStore().getLoginUser.data.user_id;
  const targetDicomFile = useDicomStore().getTargetDicomFile;

  // 새 요청 ID 생성
  const requestId = ++currentRequestId;

  // 이전 요청 취소
  if (controller) {
    controller.abort();
  }
  controller = new AbortController();

  try {
    useDicomStore().setLoading(true);
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    useViewerStore().setFileLoadState(false);

    // 1. 서버에 파일 선택 요청
    const response = await axios.post(
      `${TUBA_SERVER_URL}/file/select_file`,
      {
        user_id: userId,
        unique_filename: targetDicomFile.unique_filename,
      },
      {
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
      }
    );

    // 최신 요청이 아니면 중단
    if (requestId !== currentRequestId) return;

    // 2. .bin 파일 fetch
    const filename = targetDicomFile.unique_filename;
    const fileUrls = [
      { name: "tube", url: `${baseURL}/analysis/${filename}.bin` },
      { name: "completeCam", url: `${baseURL}/complete_cam/${filename}.bin` },
      { name: "incompleteCam", url: `${baseURL}/incomplete_cam/${filename}.bin` },
    ];

    const filePromises = fileUrls.map(async ({ name, url }) => {
      // controller가 중간에 null 되면 에러 방지
      if (!controller) throw new Error("요청이 이미 취소됨");

      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`${url} failed with status ${res.status}`);

      const buffer = await res.arrayBuffer();

      return { name, buffer };
    });

    const results = await Promise.all(filePromises);

    // 최신 요청이 아니면 중단
    if (requestId !== currentRequestId) return;

    const buffers: Record<string, ArrayBuffer> = {};
    results.forEach(({ name, buffer }) => {
      buffers[name] = buffer;
    });

    // 3. cornerstone 이미지 로딩
    const imageId = `wadouri:${baseURL}/dicom/${filename}.dcm`;
    const img = await cornerstone.loadAndCacheImage(imageId);

    // 최신 요청이 아니면 중단
    if (requestId !== currentRequestId) return;
    useDicomStore().setDicomImg(img);
    useDicomStore().setBinFiles(buffers);
    setTimeout(() => {
      useDicomStore().setLoading(false);
    }, 300);
    useLogStore().endTask("select file 기능이 성공적으로 실행되었습니다.");
    return response.data;
  } catch (error: any) {
    if (
      axios.isCancel(error) ||
      error.name === "CanceledError" ||
      error.name === "AbortError" ||
      error.message?.includes("The operation was aborted")
    ) {
      console.warn("select file 요청이 취소되었습니다.");
      useLogStore().endTask("select file 요청이 중단되었습니다.");
    } else if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0];
      const message = `select file 오류 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      useLogStore().endTask("select file 기능 사용 중 오류가 발생하였습니다.");
      console.error("Unexpected Error:", error);
    }
  } finally {
    // 현재 요청이 아직 최신 요청일 때만 controller 해제
    if (requestId === currentRequestId) {
      controller = null;
    }
  }
}

/**
 * NGTUBE SERVER
 * @method dicomTag
 */
export async function dicomTag() {
  const userId = useUsersStore().getLoginUser.data.user_id;
  const targetDicomFile = useDicomStore().getTargetDicomFile;

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.post(`${TUBA_SERVER_URL}/file/dicom_tag`, {
      user_id: userId,
      unique_filename: targetDicomFile.unique_filename
    });
    useLogStore().endTask('get dicom tag 기능이 성공적으로 실행되었습니다.');
    return response.data.dicom_tags;
  } catch (error: any) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `get dicom tag 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('get dicom tag 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
    return false;
  }
}

/**
 * NGTUBE SERVER
 * @method exportDicomFile
 */
export async function exportDicomFile(target: string, data: any) {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return; }

  const dicomStore = useDicomStore();
  const dicomFile = dicomStore.getTargetDicomFile;
  const dicomInfo = dicomStore.getTargetDicomInfo;
  const mask = dicomStore.getMaskCheckState;
  const heatmap = dicomStore.getHeatMapCheckState;
  const prob = dicomStore.getProbabilityCheckState;
  const isReviewed = dicomFile.is_reviewed;
  let scoreInfo
  if (isReviewed) {
    scoreInfo = 'Confirmed'
  } else {
    scoreInfo = 'Suggested'
  }

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const formData = new FormData();
    formData.append('unique_filename', dicomFile.unique_filename);
    formData.append('file_name', dicomFile.origin_filename);
    formData.append('image', data);
    if(target == 'origin') {
      formData.append('mode', 'grayscale');
    } else {
      formData.append('mode', 'rgb');
    }
    formData.append('window_center', (dicomInfo.window_center).toString());
    formData.append('window_width', (dicomInfo.window_width).toString());
    formData.append('mask', (mask).toString());
    formData.append('heatmap', (heatmap).toString());
    formData.append('probability_map', (prob).toString());
    formData.append('tube_threshold', (dicomInfo.tube_threshold).toString());
    formData.append('cls_score', `${dicomFile.score} [${scoreInfo}]`);
    if (dicomInfo.select_heatmap_idx == 0) {
      formData.append('select_heatmap', 'Complete');
    } else {
      formData.append('select_heatmap', 'Incomplete');
    }
    formData.append('image_weight', (dicomInfo.image_weight).toString());

    const response = await axios.post(`${TUBA_SERVER_URL}/file/export`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
    });
    // 서버에서 반환된 Blob 다운로드
    const downloadUrl = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${dicomFile.unique_filename}.dcm`;
    link.click();
    URL.revokeObjectURL(downloadUrl);

    useLogStore().endTask('export file 기능이 성공적으로 실행되었습니다.');

  } catch (error: any) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0];

      const message = `export file 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      useLogStore().endTask('export file 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
    return false;
  }
}


/**
 * NGTUBE SERVER
 * @method dicomVisualization
 */
export async function dicomVisualization() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const targetDicomFile = useDicomStore().getTargetDicomFile;
  const targetDicomInfo = useDicomStore().getTargetDicomInfo;

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.post(`${TUBA_SERVER_URL}/visualization/dicom_visualization`, {
      user_id: userId,
      unique_filename: targetDicomFile.unique_filename,
      window_center: targetDicomInfo.window_center,
      window_width: targetDicomInfo.window_width,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'arraybuffer', // 이미지나 바이너리 데이터를 받을 때 'arraybuffer'로 지정
    });

    useLogStore().endTask('dicom visualization 기능이 성공적으로 실행되었습니다.');
    const blob = new Blob([response.data], { type: 'image/png' });
    const imageUrl = URL.createObjectURL(blob);

    return imageUrl;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `dicom visualization 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('dicom visualization 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
  }
}

/**
 * NGTUBE SERVER
 * @method anaysisVisualization
 */
export async function anaysisVisualization() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const targetDicomFile = useDicomStore().getTargetDicomFile;
  const targetDicomInfo = useDicomStore().getTargetDicomInfo;
  const visualizationOption = useDicomStore().getVisualizationOption;

  const param = {
    user_id: userId,
    unique_filename: targetDicomFile.unique_filename,
    window_center: targetDicomInfo.window_center,
    window_width: targetDicomInfo.window_width,
    tube_threshold: targetDicomInfo.tube_threshold,
    image_weight: targetDicomInfo.image_weight,
    mask_check: visualizationOption.mask_check,
    heatmap_check: visualizationOption.heatmap_check,
    probability_map_check: visualizationOption.probability_map_check,
    heatmap_option: visualizationOption.heatmap_option,
  }

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.post(`${TUBA_SERVER_URL}/visualization/analysis_visualization`, param, {
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'arraybuffer',  // 이미지나 바이너리 데이터를 받을 때 'arraybuffer'로 지정
    });

    useLogStore().endTask('anaysis visualization 기능이 성공적으로 실행되었습니다.');
    const blob = new Blob([response.data], { type: 'image/png' });
    const imageUrl = URL.createObjectURL(blob);

    return imageUrl;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `anaysis visualization 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('anaysis visualization 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
  }
}

/**
 * NGTUBE SERVER
 * @method getReadingResult
 */
export async function getReadingResult() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const withCAD = useUsersStore().getWithCAD;

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.get(`${TUBA_SERVER_URL}/reading_result/get_reading_result`, {
      params: {
        user_id: userId,
        use_cad: withCAD,
      },
    });
    useLogStore().endTask('get reading result 기능이 성공적으로 실행되었습니다.');
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `get reading result 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('get reading result 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
  }
}

/**
 * NGTUBE SERVER
 * @method insertReadingResult
 */
export async function insertReadingResult() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const withCAD = useUsersStore().getWithCAD;
  const targetDicomFile = useDicomStore().getTargetDicomFile;
  const readingTime = useTimerStore().getReadingTime;
  const completeScore = useDicomStore().getCompleteScore;

  const param = {
    user_id: userId,
    use_cad: withCAD,
    origin_filename: targetDicomFile.origin_filename,
    unique_filename: targetDicomFile.unique_filename,
    patient_id: targetDicomFile.patient_id,
    patient_name: targetDicomFile.patient_name,
    study_date: targetDicomFile.study_date,
    score: completeScore,
    reading_time: readingTime,
  }

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.post(`${TUBA_SERVER_URL}/reading_result/insert_reading_result`, param, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    useLogStore().endTask(`insert reading result 기능이 성공적으로 실행되었습니다. Confirm score: ${completeScore} / Reading time: ${readingTime}`);
    recordLog(useTimerStore().getStartTime, useTimerStore().getEndTime, useTimerStore().getReadingTime, '소요 시간: ' + useTimerStore().getReadingTime + 's')
    useReadingResultStore().setReadingResultList();
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `insert reading result 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('insert reading result 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
  }
}

/**
 * NGTUBE SERVER
 * @method deleteReadingResult
 */
export async function deleteReadingResult() {
  const isSessionValid = await checkLastLoginToken();
  if (!isSessionValid) { return }

  const userId = useUsersStore().getLoginUser.data.user_id;
  const readingResultList = useReadingResultStore().getReadingResultList;
  const target = ref<any>([]);

  for (let i = 0; i < readingResultList.length; i++) {
    if (readingResultList[i].selected) {
      target.value = [...target.value, readingResultList[i].unique_filename];
    }
  }
  if (target.value.length == 0) {
    const targetDicomFile = useDicomStore().getTargetDicomFile;
    target.value.push(targetDicomFile.unique_filename);
  }

  try {
    useLoginStore().resetLogoutTimer();
    useLogStore().startTask();
    const response = await axios.delete(`${TUBA_SERVER_URL}/reading_result/delete_reading_result`, {
      data: {
        user_id: userId,
        unique_filenames: target.value,
      },
    });
    useLogStore().endTask('delete reading result 기능이 성공적으로 실행되었습니다.');
    if (response.status == 200) {
      return true;
    }
    return false;
  } catch (error: any) {
    if (error instanceof AxiosError && error.response) {
      const { status, data } = error.response;
      const detail = data?.detail?.[0]; // detail이 배열일 경우 대비

      const message = `delete reading result 기능 중 오류 발생 (Status: ${status}, Detail: ${detail?.msg}, Type: ${detail?.type})`;
      useLogStore().endTask(message);
      console.error(message);
    } else {
      // 예상치 못한 에러 처리
      useLogStore().endTask('delete reading result 기능 사용 중 오류가 발생하였습니다.');
      console.error("Unexpected Error:", error);
    }
    return false;
  }
}

/**
 * NGTUBE SERVER
 * @method recordLog
 */
export async function recordLog(startTime: any, endTime: any, readingTime: number, message: any) {
  const userId = useUsersStore().getLoginUser.data.user_id;
  const withCAD = useUsersStore().getWithCAD;
  const cadStatus = ref('');
  const targetDicomFile = useDicomStore().getTargetDicomFile;

  if (withCAD) {
    cadStatus.value = 'withCAD';
  } else {
    cadStatus.value = 'withoutCAD';
  }

  const param = {
    user_id: userId,
    message: message,
    start_time: startTime,
    end_time: endTime,
    cad_status: cadStatus.value,
    elapsed_time: readingTime,
    origin_filename: targetDicomFile?.origin_filename ?? '',
    unique_filename: targetDicomFile?.unique_filename ?? ''
  }

  try {
    const response = await axios.post(`${TUBA_SERVER_URL}/log/logs`, param, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * NGTUBE SERVER
 * @method getDiskInfo
 */
export async function getDiskInfo() {
  try {
    const response = await axios.get(`${TUBA_SERVER_URL}/disk/disk-info`);
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}

/**
 * PACS
 * @method getPacsData
 */
export async function getPacsData(id: string, name: string, studyDate: string, page: number) {
  try {
    useLoginStore().resetLogoutTimer();
    const response = await axios.get(`${TUBA_SERVER_URL}/pacs/search`, {
      params: {
        "patient_id": id,
        "patient_name": name,
        "study_date": studyDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
  }
}
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                   
/**
 * PACS
 * @method uploadPacsData
 */
export async function uploadPacsData() {
  const withCAD = useUsersStore().getWithCAD;
  const userId = useUsersStore().getLoginUser.data.user_id;
  const currPageData = usePacsStore().getCurrPageData;

  const selectedItems = currPageData.filter((item: PacsItem) => item.selected);
  const uploadProgress = 100 / selectedItems.length;

  useDicomStore().setPendingCount(selectedItems.length);
  useLoginStore().resetLogoutTimer();

  
  for (const selectedItem of selectedItems) {
    try {
      const response = await axios.post(`${TUBA_SERVER_URL}/pacs/upload`, {
        user_id: userId,
        sop_instance_uid: selectedItem.SOPInstanceUID,
        use_cad: withCAD
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      usePacsStore().setUploadProgress(uploadProgress);
    } catch (error: any) {
      console.error('Error uploading PACS data:', error);
      return false;
    }
  }
  return true;
}

/**
 * PACS
 * @method getPacsThumbnail
 */
let currentController: AbortController | null = null;
export async function getPacsThumbnail(uid: string) {
  useLoginStore().resetLogoutTimer();
  try {
    if (currentController) {
      currentController.abort(); // 이전 요청이 진행 중이면 취소
    }

    currentController = new AbortController();

    const response = await axios.get(`${TUBA_SERVER_URL}/pacs/thumbnail/${uid}`, {
      responseType: 'blob',
      signal: currentController.signal,
    });

    const imageUrl = URL.createObjectURL(response.data);
    currentController = null; // 완료 후 controller 초기화

    return imageUrl;
  } catch (error: any) {
    if (axios.isCancel(error) || error.name === 'CanceledError') {
      console.log('요청이 취소됨');
    } else {
      console.error('Error fetching thumbnail:', error);
    }
    return null;
  }
}

/**
 * PACS
 * @method getPacsConfig
 */
export async function getPacsConfig() {
  
  try {
    useLoginStore().resetLogoutTimer();
    const response = await axios.get(`${TUBA_SERVER_URL}/pacs/config/`, {
    });

    usePacsStore().setConnectState(response.data.is_connected)
    return response.data;
  } catch (error: any) {
    console.error('Error:', error);
    return null;
  }
}

/**
 * PACS
 * @method updatePacsConfig
 */
export async function updatePacsConfig() {
  const pacsConfig = usePacsStore().getPacsConfigInfo;
  try {
    useLoginStore().resetLogoutTimer();
    const response = await axios.patch(`${TUBA_SERVER_URL}/pacs/config/`, null, {
      params: {
        PACS_AET: pacsConfig.title,
        PACS_IP: pacsConfig.ip,
        PACS_PORT: pacsConfig.port
      }
    });
    await getPacsConfig();

    return response.data.is_connected;
  } catch (error: any) {
    console.error('Error:', error);
    return null;
  }
}
